<!-- FT-216 REQ-10, REQ-15, CTR-07: виджет главной «Динамическое ценообразование» (superadmin, admin).
     Сверху вниз: карточки открытых рекомендаций, «Применено за 7 дней» с откатом, пустые состояния. Доски «Свободно
     на ближайшие дни» внутри нет — это отдельный виджет FT-217 (USER 2026-10-26: с ней виджет «как будто скрещен»,
     рекомендации уходили под прокрутку). Высоту ячейки сетки подгоняет главная по замеру содержимого (событие
     content-height, как FT-217 SC-08); выше DP_WIDGET_MAX_PX — прокрутка внутри. Выключено — ссылка на настройки, применённые
     остаются. Без услуги в плане — только «Применено за 7 дней» (REQ-15). После отрисовки карточек — POST shown
     с id показанных (первый показ, REQ-12). На телефоне — одна колонка. -->
<template>
  <v-card class="dp-widget" data-testid="dynamic-pricing-widget">
    <v-card-title class="dp-widget__title">{{ $t("widgets.dynamic_pricing") }}</v-card-title>
    <v-card-subtitle class="pb-2 dp-widget__subtitle">{{ $t("dynamic_pricing.widget_subtitle") }}</v-card-subtitle>
    <v-card-text class="px-3 pb-3 dp-widget__body">
      <div ref="content">
      <div v-if="!data" class="text-caption text--secondary" data-testid="dp-widget-no-data">
        {{ $t("dynamic_pricing.widget_no_data") }}
      </div>
      <template v-else>
        <template v-if="inPlan">
          <div v-if="!enabled" class="d-flex align-center flex-wrap mb-3" style="gap: 8px" data-testid="dp-widget-disabled">
            <span class="text-body-2 text--secondary">{{ $t("dynamic_pricing.disabled") }}</span>
            <v-btn x-small outlined color="primary" data-testid="dp-widget-settings" @click="openSettings">
              {{ $t("dynamic_pricing.open_settings") }}
            </v-btn>
          </div>
          <template v-else>
            <!-- Групп нет — рекомендаций не будет: не «Цены в порядке», а куда идти собрать группы -->
            <div v-if="noGroups" class="d-flex align-center flex-wrap mb-3" style="gap: 8px" data-testid="dp-widget-no-groups">
              <span class="text-body-2 text--secondary">{{ $t("price_groups.not_built") }}</span>
              <v-btn x-small outlined color="primary" data-testid="dp-widget-settings" @click="openSettings">
                {{ $t("dynamic_pricing.open_settings") }}
              </v-btn>
            </div>
            <div v-else-if="open.length" data-testid="dp-widget-open">
              <recommendation-card v-for="rec in open" :key="rec.id" :rec="rec" :unit="state.unit"
                :busy="!!busyId" @apply="askApply" @reject="reject" @suppress="suppress" />
            </div>
            <div v-else class="mb-3" data-testid="dp-widget-ok">
              <div class="text-body-1">
                <v-icon small color="success" class="mr-1">mdi-check-circle-outline</v-icon>{{ $t("dynamic_pricing.prices_ok") }}
              </div>
              <div v-if="nextCheckText" class="text-caption text--secondary" data-testid="dp-widget-next-check">
                {{ $t("dynamic_pricing.next_check", { at: nextCheckText }) }}
              </div>
            </div>

            <div v-if="insufficient.length" class="mb-3" data-testid="dp-widget-insufficient">
              <span class="text-caption text--secondary mr-1">{{ $t("dynamic_pricing.insufficient_data") }}:</span>
              <v-chip v-for="g in insufficient" :key="g.id" x-small label class="mr-1 mb-1">{{ groupLabel(g) }}</v-chip>
            </div>
          </template>
        </template>

        <applied-recommendations :items="applied" :unit="state.unit" @changed="refresh" />
      </template>
      </div>
    </v-card-text>

    <v-dialog v-model="applyDialog" max-width="440" :persistent="!!busyId">
      <v-card data-testid="dp-apply-dialog">
        <v-card-title class="text-h6">{{ $t("dynamic_pricing.apply_title") }}</v-card-title>
        <v-card-text>{{ $t("dynamic_pricing.apply_warning") }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="!!busyId" data-testid="dp-apply-cancel" @click="applyDialog = false">{{ $t("custom.cancel") }}</v-btn>
          <v-btn color="primary" :loading="!!busyId" data-testid="dp-apply-confirm" @click="confirmApply">
            {{ $t("dynamic_pricing.rec.apply") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import moment from "moment";
import RecommendationCard from "@/components/dashboard/dynamic_pricing/RecommendationCard.vue";
import AppliedRecommendations from "@/components/dashboard/dynamic_pricing/AppliedRecommendations.vue";
import {
  APPLIED_STATUSES,
  countOf,
  dynamicPricingApi,
  dynamicPricingInPlan,
  mergeRecommendations,
  normalizeRecommendations,
  reportRecommendationActionError,
  reportDynamicPricingLoadError,
} from "@/api/dynamicPricing";
import { priceGroupLabelOptions } from "@/utils/priceGroupLabel";
import { formatShortDates, recommendationGroupLabel } from "@/utils/dynamicPricingFormat";

// Выше ячейка не растёт — дальше прокрутка внутри (≈ четыре карточки рекомендаций; 16 рядов сетки).
export const DP_WIDGET_MAX_PX = 630;

export default {
  name: "DynamicPricingWidget",
  components: { RecommendationCard, AppliedRecommendations },
  props: {
    // dynamic_pricing из dashboard_data (CTR-07); null — данных ещё нет. Из free_cars_upcoming берётся только has_groups.
    data: { type: Object, default: null },
  },
  data() {
    return {
      state: {}, // нормализованный ответ; после действий — дополняется GET /recommendations
      busyId: null,
      applyDialog: false,
      applyTarget: null,
    };
  },
  computed: {
    // REQ-15, CTR-10: услуга в плане — по module_available ответа сервера (стор с планом мог устареть); поля в ответе
    // нет — по ключу dynamic_pricing в плане из стора (тот же предикат, что у блока настроек).
    inPlan() {
      if (typeof this.state.module_available === "boolean") return this.state.module_available;
      return dynamicPricingInPlan(this.$store && this.$store.getters.getMainCompany);
    },
    enabled() {
      return this.state.enabled !== false;
    },
    open() {
      return this.inPlan && this.enabled ? this.state.open || [] : [];
    },
    applied() {
      return this.state.applied || [];
    },
    insufficient() {
      return this.state.insufficient || [];
    },
    // У филиала не собраны ценовые группы FT-217 (has_groups из dashboard_data) — рекомендаций быть не может.
    noGroups() {
      const free = this.data && this.data.free_cars_upcoming;
      return !!free && free.has_groups === false;
    },
    // next_check_at — 07:00 по времени филиала с его смещением: показываем время филиала, не пояс браузера.
    nextCheckText() {
      const at = this.state.next_check_at;
      const m = at ? moment.parseZone(at) : null;
      return m && m.isValid() ? m.format("DD.MM HH:mm") : "";
    },
  },
  watch: {
    data: {
      immediate: true,
      handler(value) {
        this.state = value ? normalizeRecommendations(value) : {};
      },
    },
    // Первый показ — при любой смене показанных карточек: новые данные, перечитанный список, услуга или «включено»
    // поменялись в ответе.
    open: {
      immediate: true,
      handler() {
        this.reportShown();
      },
    },
  },
  mounted() {
    // Высота содержимого меняется с рекомендациями, раскрытием машин, шириной ячейки: ResizeObserver на обёртке
    // содержимого; без него — resize окна (как FreeCarsUpcomingWidget).
    if (typeof ResizeObserver !== "undefined" && this.$refs.content) {
      this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
      this.resizeObserver.observe(this.$refs.content);
    } else if (typeof window !== "undefined") {
      this.onWindowResize = () => this.scheduleMeasure();
      window.addEventListener("resize", this.onWindowResize);
    }
    this.scheduleMeasure();
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.onWindowResize) window.removeEventListener("resize", this.onWindowResize);
  },
  beforeCreate() {
    // id, о показе которых уже сообщили серверу (повтор не нужен: сервер пишет только первый показ).
    // beforeCreate, а не created: immediate-наблюдатель data срабатывает раньше created.
    this.shownIds = new Set();
  },
  methods: {
    scheduleMeasure() {
      this.$nextTick(() => this.measure());
    },
    // Сколько пикселей нужно карточке без прокрутки: заголовок + подзаголовок + содержимое + отступы тела. Меряется
    // обёртка содержимого, а не scrollHeight тела — тот не меньше высоты ячейки, и выросший виджет не смог бы уменьшиться.
    // Главная переводит пиксели в ряды сетки.
    measure() {
      // v-card-title / v-card-subtitle / v-card-text — функциональные компоненты, ref на них не ставится
      const title = this.$el && this.$el.querySelector(".dp-widget__title");
      const subtitle = this.$el && this.$el.querySelector(".dp-widget__subtitle");
      const body = this.$el && this.$el.querySelector(".dp-widget__body");
      const content = this.$refs.content;
      if (!title || !body || !content || !content.offsetHeight) return; // не в DOM / скрыт — мерить нечего
      const style = window.getComputedStyle(body);
      const pad = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
      const head = title.offsetHeight + (subtitle ? subtitle.offsetHeight : 0);
      const px = Math.min(DP_WIDGET_MAX_PX, Math.ceil(head + content.offsetHeight + pad));
      if (px === this.lastHeight) return;
      this.lastHeight = px;
      this.$emit("content-height", px);
    },
    groupLabel(group) {
      return recommendationGroupLabel({ group }, priceGroupLabelOptions(this, this.state.unit));
    },
    openSettings() {
      // вкладка «Цены» — первая в настройках компании
      if (this.$store && this.$store.commit) this.$store.commit("setCompanyProfileTab", 0);
      this.$router.push({ name: "CompanyProfile" }).catch(() => {});
    },
    // Первый показ (REQ-12): после отрисовки — id показанных карточек, каждый один раз за жизнь виджета. В shownIds id
    // попадает на время запроса (повтор не уходит параллельно) и остаётся только после успеха: не прошёл — следующая
    // отрисовка отправит его снова.
    reportShown() {
      this.$nextTick(() => {
        const ids = this.open.map((r) => r.id).filter((id) => id != null && !this.shownIds.has(id));
        if (!ids.length || !this.axios) return;
        ids.forEach((id) => this.shownIds.add(id));
        dynamicPricingApi.markShown(this.axios, ids).catch((error) => {
          ids.forEach((id) => this.shownIds.delete(id));
          // метрика, не действие человека: без тоста, но и не молча
          if (this.setError) this.setError(error, this.$t("dynamic_pricing.load_error"), { silent: true });
        });
      });
    },
    async refresh() {
      try {
        const next = await dynamicPricingApi.listRecommendations(this.axios);
        this.state = mergeRecommendations(this.state, next);
      } catch (error) {
        reportDynamicPricingLoadError(this, error, this.$t("dynamic_pricing.load_error"));
      }
    },
    askApply(rec) {
      this.applyTarget = rec;
      this.applyDialog = true;
    },
    async confirmApply() {
      const rec = this.applyTarget;
      if (!rec || this.busyId) return;
      await this.act(rec, dynamicPricingApi.applyRecommendation, this.$t("dynamic_pricing.apply_error"), (result) => {
        // Не open — сервер отвечает текущим статусом без сдвига: applied с пустым applied_cars — уже применена
        // (другим или автоприменением), настоящее применение всегда сдвигает хотя бы одну машину (иначе expired).
        const appliedCars = countOf(result.applied_cars);
        if (APPLIED_STATUSES.includes(result.status) && appliedCars > 0) {
          const notes = [];
          const excluded = countOf(result.excluded_cars);
          if (excluded > 0) notes.push(this.$t("dynamic_pricing.excluded_cars", { n: excluded }));
          if (Array.isArray(result.skipped_dates) && result.skipped_dates.length) {
            notes.push(this.$t("dynamic_pricing.skipped_dates", { dates: formatShortDates(result.skipped_dates) }));
          }
          this.notify(this.$t("dynamic_pricing.applied_n", { n: appliedCars }), notes.join(" "), notes.length ? "warning" : "success");
        } else {
          this.notifyNotOpen(result.status);
        }
      });
      this.applyDialog = false;
    },
    reject(rec) {
      return this.act(rec, dynamicPricingApi.rejectRecommendation, this.$t("dynamic_pricing.action_error"), (result) => {
        if (result.status === "rejected") this.notify(this.$t("dynamic_pricing.rejected"), "", "success");
        else this.notifyNotOpen(result.status);
      });
    },
    // expired (дни прошли, все машины за границами, выключено, группа архивирована) — «больше не актуальна»;
    // прочие статусы — уже решена другим (применена, отклонена, заглушена, откачена).
    notifyNotOpen(status) {
      const key = status === "expired" ? "dynamic_pricing.not_actual" : "dynamic_pricing.already_decided";
      this.notify(this.$t(key), "", "warning");
    },
    // suppress → { status, suppressed_group_ids }: группа заглушается и для уже решённой рекомендации.
    suppress(rec) {
      return this.act(rec, dynamicPricingApi.suppressRecommendation, this.$t("dynamic_pricing.action_error"), () => {
        this.notify(this.$t("dynamic_pricing.suppressed"), this.$t("dynamic_pricing.suppressed_hint"), "success");
      });
    },
    // Действие над рекомендацией: запрос → текст результата → перечитать список (и при отказе: статус мог смениться).
    async act(rec, call, errorTitle, onSuccess) {
      if (this.busyId) return;
      this.busyId = rec.id;
      try {
        const result = (await call(this.axios, rec.id)) || {};
        onSuccess(result);
      } catch (error) {
        reportRecommendationActionError(this, error, errorTitle);
      } finally {
        await this.refresh();
        this.busyId = null;
      }
    },
    notify(...args) {
      if (this.$notify) this.$notify(...args);
    },
  },
};
</script>

<style scoped>
.dp-widget {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.dp-widget__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
</style>
